import {
  add,
  differenceInHours,
  differenceInSeconds,
  endOfMinute,
  isBefore,
  isSameMonth,
  sub,
} from 'date-fns';

import {
  chart_resolutions,
  getBestResolution,
  getTime,
  startOfTime,
} from '../../../../utils/chart';
import formatMetricsQuery, {
  buildApiGranularity,
  Granularity,
} from '../../../../utils/formatMetricsQuery';
import { hasSeconds, roundUpHour } from '../../../../utils/time';
import { formatDate } from '../../../../utils/date';
import { ChartDatapoint } from '../../../common/Chart/Chart';
import { getSampledMaxBucketValue } from '../../../common/Chart/fillSampledMetricGaps';
import {
  metric_definitions,
  metric_time_units,
  MetricName,
  MetricOptions,
  MetricType,
  TimeUnit,
} from '../../../common/metrics/metric-definitions';
import useMetricsQuery from '../../../hooks/useMetricsQuery';
import { useDatePreferences } from '../../../hooks/useDatePreferences';
import { DateRange } from './MetricDatePicker';

export const OTHER_SERIES_KEY = '__other__';
export const MAX_DIMENSION_SERIES = 10;
export interface TableData {
  resource_id: string;
  value: number;
}

export type MetricChartType = 'chart:line' | 'chart:bar';
export type MetricDisplayType = 'card' | 'table' | MetricChartType;

export interface ChartData {
  data: ChartDatapoint[];
  connect_nulls?: boolean;
  total: number;
  highest: number;
  average: number;
  samples_with_data?: number;
  time_unit_configs: TimeUnit;
}

export type MetricData = {
  data: Record<string, ChartData> | ChartData | TableData[] | number | string | null;
  refetch: () => void;
};

export interface MetricConfigs {
  options?: MetricOptions;
  time_unit?: keyof typeof metric_time_units;
  date_range?: DateRange;
  force_rate_as?: 'minute' | 'second';
  revalidateOnFocus?: boolean;
}

const RATE_MINUTE_THRESHOLD = 2;

const getDateFormat = (start_date: Date, end_date: Date, granularity: Granularity) => {
  let base = 'HH:mm';

  // const has_minutes = hasMinutes(start_date) || hasMinutes(end_date);
  const has_seconds = hasSeconds(start_date) || hasSeconds(end_date) || granularity === 'second';

  if (has_seconds) {
    base = `${base}:ss`;
  }

  if (Math.abs(differenceInHours(start_date, end_date)) > 30) {
    base = `MM/dd ${base}`;
  }

  if (!isSameMonth(start_date, end_date)) {
    base.replace('eee', 'MMM');
  }

  return base;
};

// The reference date is the user-controlled refresh timestamp. Keeping clock
// access outside this function makes query ranges deterministic across renders.
export const formatDateRange = (
  reference_date: Date,
  date_range?: DateRange,
  time_unit?: keyof typeof metric_time_units,
) => {
  if (time_unit) {
    const configs = metric_time_units[time_unit];

    const now = add(endOfMinute(reference_date), { seconds: 1 });

    const minutes = now.getMinutes();
    const minutes_to_add_for_padding =
      minutes % configs.sampling.value === 0
        ? 0
        : minutes - (minutes % configs.sampling.value) + configs.sampling.value - minutes;
    const end_time = add(now, { minutes: minutes_to_add_for_padding });

    const start_date = sub(end_time, { [configs.unit]: configs.value });
    const end_date = end_time;

    const period_in_sample_granularity = configs.value * 60;
    const samples = period_in_sample_granularity / configs.sampling.value;

    return { start_date, end_date, configs: metric_time_units[time_unit], samples };
  }

  let start_date = new Date(date_range!.min!);
  let end_date = new Date(date_range!.max!);

  const diff = differenceInSeconds(end_date, start_date);

  let chart_resolution = getBestResolution(diff, 60, chart_resolutions).best;

  // if the granularity is hour, always round up so the time is greater then the time inputted
  if (chart_resolution.granularity === 'hour') {
    start_date = roundUpHour(start_date);
    end_date = roundUpHour(end_date);
    const diff = differenceInSeconds(end_date, start_date);
    chart_resolution = getBestResolution(diff, 60, chart_resolutions).best;
  }

  const now = reference_date;
  const to_sub = getTime[chart_resolution.granularity](now) % chart_resolution.unit;
  const whatever = sub(now, { [`${chart_resolution.granularity}s`]: to_sub });

  const max_end_date = add(whatever, {
    [`${chart_resolution.granularity}s`]: chart_resolution.unit,
  });

  if (isBefore(max_end_date, end_date)) {
    end_date = max_end_date;
  }

  if (chart_resolution.granularity !== 'second') {
    end_date = add(startOfTime[chart_resolution.granularity](end_date), {
      [`${chart_resolution.granularity}s`]:
        (getTime[chart_resolution.granularity](end_date) * chart_resolution.unit) /
        chart_resolution.unit,
    });
    start_date = sub(end_date, {
      [`${chart_resolution.granularity}s`]: chart_resolution.steps * chart_resolution.unit,
    });
  }

  const configs: TimeUnit = {
    value: chart_resolution.unit,
    unit: `${chart_resolution.granularity}s`,
    sampling: {
      value: chart_resolution.unit,
      granularity: chart_resolution.granularity,
      date_format: getDateFormat(start_date, end_date, chart_resolution.granularity),
      priority_display: [
        getTime[chart_resolution.granularity],
        [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55],
      ],
    },
  };

  return { start_date, end_date, configs, samples: chart_resolution.steps };
};

const formatChartRawData = (
  obj,
  end_date,
  time_unit_configs,
  samples,
  values,
  query,
  index: number,
  metric_type: MetricType,
  is_sampled_metric: boolean,
  timezone = 'UTC',
) => {
  const step_start = sub(end_date, {
    [`${time_unit_configs.sampling.granularity}s`]:
      time_unit_configs.sampling.value * (samples - index),
  });

  const step_end = add(step_start, {
    [`${time_unit_configs.sampling.granularity}s`]: time_unit_configs.sampling.value,
  });

  const time_frame_values = values.filter(
    (d) =>
      new Date(d[query.timeDimensions[0].dimension]).getTime() < step_end.getTime() &&
      new Date(d[query.timeDimensions[0].dimension]).getTime() >= step_start.getTime(),
  );

  const time_frame_count = time_frame_values.reduce((count, d) => {
    if (!d[query.measures[0]]) {
      return count;
    }
    return count + parseFloat(d[query.measures[0]]);
  }, 0);
  const count_measure = metric_type === 'percent' ? query.measures[1] : undefined;
  const has_sample_data = count_measure
    ? time_frame_values.some((d) => parseFloat(d[count_measure] || '0') > 0)
    : time_frame_values.length > 0;

  const [priority_display_method, priority_display_values] =
    time_unit_configs.sampling.priority_display;
  const priority_display_value = priority_display_method(step_start);

  let y = time_frame_count;
  if (metric_type === 'rate') {
    y = time_frame_count / differenceInSeconds(step_end, step_start);
  } else if (metric_type === 'average') {
    y = time_frame_count / time_frame_values.length;
  } else if (metric_type === 'percent') {
    y = has_sample_data ? time_frame_count / time_unit_configs.sampling.value : Number.NaN;
  } else if (metric_type === 'max') {
    const metric_values = time_frame_values.map((d) => parseFloat(d[query.measures[0]]));
    const previous_value = obj.data[obj.data.length - 1]?.y;
    const max_value = is_sampled_metric
      ? getSampledMaxBucketValue(metric_values, previous_value)
      : metric_values.length === 0
        ? 0
        : Math.max(...metric_values);
    // Round to 1 decimal place for cleaner display
    y = Math.round(max_value * 10) / 10;
  }

  obj.data.push({
    priority_display: priority_display_values.some((value) => value === priority_display_value),
    sample_range: {
      min: step_start.toISOString(),
      max: step_end.toISOString(),
    },
    x: formatDate(step_start, time_unit_configs.sampling.date_format, timezone),
    y: y,
  });

  if (!isNaN(y)) {
    obj.total += y;
    obj.highest = Math.max(obj.highest, y);

    if (metric_type === 'percent') {
      obj.samples_with_data += has_sample_data ? 1 : 0;
      obj.average = obj.samples_with_data > 0 ? obj.total / obj.samples_with_data : 0;
    } else {
      obj.average = obj.total / obj.data.filter((d) => !isNaN(d.y)).length;
    }
  }

  obj.time_unit_configs = time_unit_configs;

  return obj;
};

const aggregateOtherSeries = (
  others: ChartData[],
  metric_type: MetricType,
  is_sampled_metric: boolean,
): ChartData => {
  const first = others[0];
  const sample_count = first.data.length;
  const data: ChartDatapoint[] = [];

  for (let i = 0; i < sample_count; i++) {
    const ys: number[] = [];
    for (const series of others) {
      const point = series.data[i];
      const y = point?.y;
      if (typeof y === 'number' && !isNaN(y)) {
        ys.push(y);
      }
    }

    let y: number;
    if (ys.length === 0) {
      y = metric_type === 'max' && !is_sampled_metric ? 0 : NaN;
    } else if (metric_type === 'count' || metric_type === 'rate') {
      y = ys.reduce((a, b) => a + b, 0);
    } else if (metric_type === 'max') {
      y = Math.max(...ys);
    } else {
      // average, percent: unweighted mean across the remaining series
      y = ys.reduce((a, b) => a + b, 0) / ys.length;
    }

    data.push({
      x: first.data[i].x,
      y,
      priority_display: first.data[i].priority_display,
      sample_range: first.data[i].sample_range,
    });
  }

  let total = 0;
  let highest = 0;
  let valid_count = 0;
  for (const d of data) {
    if (typeof d.y === 'number' && !isNaN(d.y)) {
      total += d.y;
      if (d.y > highest) highest = d.y;
      valid_count += 1;
    }
  }

  return {
    data,
    total,
    highest,
    average: valid_count > 0 ? total / valid_count : 0,
    time_unit_configs: first.time_unit_configs,
    connect_nulls: first.connect_nulls,
  };
};

const limitDimensionSeries = (
  values: Record<string, ChartData>,
  metric_type: MetricType,
  is_sampled_metric: boolean,
  max: number,
): Record<string, ChartData> => {
  const entries = Object.entries(values);
  if (entries.length <= max) {
    return values;
  }

  // Rank by total volume so low-activity dimensions collapse into "Other",
  // regardless of the metric type's natural aggregate (highest/average).
  const sorted = [...entries].sort((a, b) => b[1].total - a[1].total);

  const top = sorted.slice(0, max);
  const rest = sorted.slice(max);

  const result: Record<string, ChartData> = {};
  for (const [key, value] of top) {
    result[key] = value;
  }
  result[OTHER_SERIES_KEY] = aggregateOtherSeries(
    rest.map(([, v]) => v),
    metric_type,
    is_sampled_metric,
  );
  return result;
};

export const useMetric = <
  T extends Record<string, ChartData> | ChartData | TableData[] | number | string | null,
>(
  type: MetricDisplayType,
  metric: MetricName,
  refresh_key: number,
  configs: MetricConfigs,
) => {
  const { timezone } = useDatePreferences();
  const { options = {}, time_unit, date_range, force_rate_as, revalidateOnFocus } = configs;
  const {
    getQueryParams,
    fill_missing_after_zero,
    sampling_interval_seconds,
    valueToNumber,
    type: metric_type,
  } = metric_definitions[metric];
  const is_sampled_metric = fill_missing_after_zero === true;
  const is_chart = type.includes('chart');

  const {
    start_date,
    end_date,
    configs: time_unit_configs,
    samples,
  } = formatDateRange(new Date(refresh_key), date_range, time_unit);

  // Empty buckets are expected when zooming in beyond the metric's collection interval.
  const bucket_seconds = differenceInSeconds(
    add(start_date, {
      [`${time_unit_configs.sampling.granularity}s`]: time_unit_configs.sampling.value,
    }),
    start_date,
  );
  const connect_nulls =
    sampling_interval_seconds !== undefined && bucket_seconds < sampling_interval_seconds;

  const { metrics_query, query, dimensions } = (() => {
    const requested_granularity = is_chart ? time_unit_configs.sampling.granularity : undefined;
    const query_params = getQueryParams(
      {
        ...options,
        // Type assertion needed: MetricOptions.filters doesn't include 'date',
        // but the underlying QueryParams type accepts EventListFiltersProps | RequestListFiltersProps
        // which do have date fields. This merges the metric-specific filters with the required date range.
        filters: {
          ...options.filters,
          date: {
            min: start_date.toISOString(),
            max: end_date.toISOString(),
          },
        } as any,
      },
      requested_granularity,
    );

    const metrics_query = formatMetricsQuery(query_params.model, {
      ...query_params.params,
      limit: 50000,
    });

    // Match server bucket size to the chart's display bucket so we don't fetch
    // 30x the data we render (e.g. 1m buckets aggregated into 30m segments).
    if (
      metrics_query &&
      is_chart &&
      requested_granularity &&
      query_params.params.granularity === requested_granularity
    ) {
      metrics_query.query_params.granularity = buildApiGranularity(
        requested_granularity,
        time_unit_configs.sampling.value,
      );
    }

    // Create a query object compatible with formatChartRawData
    // This provides the field names needed for data access
    const query = metrics_query
      ? {
          timeDimensions: [
            {
              dimension: `${metrics_query.model}.${metrics_query.time_dimension}`,
            },
          ],
          measures: metrics_query.query_params.measures.map(
            (measure) => `${metrics_query.model}.${measure}`,
          ),
          dimensions:
            metrics_query.query_params.dimensions?.map((dim) => `${metrics_query.model}.${dim}`) ||
            [],
        }
      : null;

    return {
      metrics_query,
      query,
      dimensions: query_params.params.dimensions,
    };
  })();

  const { raw_data, refetch, is_loading, error } = useMetricsQuery<{
    [key: string]: string;
  }>(metrics_query, {
    resetResultSetOnChange: true,
    refreshKey: refresh_key,
    ...(revalidateOnFocus !== undefined && { revalidateOnFocus }),
  });

  const [data, rate_as] = (() => {
    if (!raw_data || !query) {
      return [null, null];
    }
    if (raw_data.length === 0) {
      return [null, null];
    }

    let rate_as = force_rate_as || null;

    if (type === 'card') {
      let value = raw_data.reduce((count, d) => {
        if (metric_type === 'max') {
          return parseFloat(d[query.measures[0]] || '0');
        }
        let sample_value = parseFloat(d[query.measures[0]] || '0');

        if (metric_type === 'rate') {
          sample_value = sample_value / differenceInSeconds(end_date, start_date);
        }

        return count + sample_value;
      }, 0);

      if (typeof value === 'string') {
        return [value];
      }

      if (metric_type === 'rate') {
        if (!rate_as && value < RATE_MINUTE_THRESHOLD) {
          rate_as = value < RATE_MINUTE_THRESHOLD ? 'minute' : 'second';
        }

        if (rate_as === 'minute') {
          value = value * 60;
        }
      }

      return [value, rate_as];
    }
    if (type === 'table') {
      const dimension = query.dimensions[0];
      const measure = query.measures[0];
      let data: { resource_id: string; value: number }[] = [];
      for (const d of raw_data) {
        if (!d[measure]) {
          continue;
        }
        data.push({
          resource_id: d[dimension],
          value: valueToNumber(d[measure]),
        });
      }

      if (metric_type === 'rate') {
        if (!rate_as) {
          rate_as = !data.some((value) => value.value > RATE_MINUTE_THRESHOLD)
            ? 'minute'
            : 'second';
        }

        if (rate_as === 'minute') {
          data = data.map((d) => ({ ...d, value: d.value * 60 }));
        }
      }
      return [data.sort((a: any, b: any) => b.value - a.value), rate_as];
    }
    const dimension = dimensions && dimensions[0];
    if (is_chart && dimension && raw_data.length > 0) {
      const data_by_dimension = raw_data.reduce(
        (obj, d) => {
          // Keep an absent dimension distinct from a literal "null" value so
          // chart legends can consistently render it as "Unknown".
          const id = d[query.dimensions[0]] ?? '';
          if (obj[id]) {
            obj[id] = [...obj[id], d];
            return obj;
          }
          obj[id] = [d];
          return obj;
        },
        {} as Record<
          string,
          {
            [key: string]: string;
          }[]
        >,
      );

      const raw_values = Object.entries(data_by_dimension).reduce(
        (obj, [key, values]) => {
          Array.from(Array(samples).keys()).reduce(
            (inner_obj, index) => {
              inner_obj = formatChartRawData(
                inner_obj,
                end_date,
                time_unit_configs,
                samples,
                values,
                query,
                index,
                metric_type,
                is_sampled_metric,
                timezone,
              );

              obj[key] = inner_obj;

              return inner_obj;
            },
            {
              data: [],
              connect_nulls,
              time_unit_configs,
              total: 0,
              highest: 0,
              average: 0,
              samples_with_data: 0,
            },
          ) as ChartData;

          return obj;
        },
        {} as Record<string, ChartData>,
      );

      const values = limitDimensionSeries(
        raw_values,
        metric_type,
        is_sampled_metric,
        MAX_DIMENSION_SERIES,
      );

      if (metric_type === 'rate') {
        if (!rate_as) {
          rate_as = !Object.values(values).some((value) => value.highest > RATE_MINUTE_THRESHOLD)
            ? 'minute'
            : 'second';
        }

        if (rate_as === 'minute') {
          Object.keys(values).forEach((key) => {
            values[key] = {
              ...values[key],
              data: values[key].data.map((d) => ({ ...d, y: (d.y as number) * 60 })),
              total: values[key].total * 60,
              highest: values[key].highest * 60,
              average: values[key].average * 60,
            };
          });
        }
      }

      return [values, rate_as];
    }
    if (is_chart) {
      let values = Array.from(Array(samples).keys()).reduce(
        (obj, index) => {
          return formatChartRawData(
            obj,
            end_date,
            time_unit_configs,
            samples,
            raw_data,
            query,
            index,
            metric_type,
            is_sampled_metric,
            timezone,
          );
        },
        {
          data: [],
          connect_nulls,
          time_unit_configs,
          total: 0,
          highest: 0,
          average: 0,
          samples_with_data: 0,
        },
      ) as ChartData;

      if (metric_type === 'rate') {
        if (!rate_as) {
          rate_as = values.highest < RATE_MINUTE_THRESHOLD ? 'minute' : 'second';
        }

        if (rate_as === 'minute') {
          values = {
            ...values,
            data: values.data.map((d) => ({ ...d, y: (d.y as number) * 60 })),
            total: values.total * 60,
            highest: values.highest * 60,
            average: values.average * 60,
          };
        }
      }

      return [values, rate_as];
    }
    return [];
  })();

  return { data: is_loading ? undefined : (data as T), rate_as, refetch, error, is_loading };
};
